import { useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { useDarkMode } from "../../../context/ThemeContext";
import { buildAccounts } from "../../../features/sg/cuadre";
import { useCuadreUnits } from "../../../features/sg/cuadreSource";
import { useRetransmissions } from "../../../features/sg/retransmissions";
import {
  descuadresSheets,
  downloadHomologado,
  downloadHomologadoZip,
  exportBarrido,
  exportLineas,
  exportUsuarios,
  facturacionSheet,
  platformsSheets,
  unitsSheet,
  usuariosHeaders,
} from "../../../features/sg/informes";
import { barridoMonth, cutoffFor } from "../../../features/sg/homologado";
import { downloadNativoWialon } from "../../../features/sg/barridoNativo";
import { downloadXlsxSheets } from "../../../features/sg/xlsx";
import { fileDate } from "../../../features/sg/exportRows";
import { notifyError, notifyOk } from "../../../features/sg/notify";
import { PLATFORMS, PLATFORM_META, type SgPlatform } from "../../../features/sg/platforms";
import { PlatformLogo } from "../components/PlatformLogo";
import { fmt } from "../cuadre-section/ui";

type ReportKey = "cuadre" | "descuadres" | "plataformas" | "facturacion" | "barrido" | "lineas" | "usuarios";

// Últimos 12 meses de barrido; el de este mes corta el último día del anterior.
function monthOptions(now = new Date()) {
  return Array.from({ length: 12 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    return { value: `${d.getFullYear()}-${d.getMonth()}`, cutoff: cutoffFor(d.getFullYear(), d.getMonth()) };
  });
}

export default function Informes() {
  const { t } = useTranslation("sg");
  const { isDarkMode } = useDarkMode();
  const { source, units, progress } = useCuadreUnits();
  const retrans = useRetransmissions();
  const [running, setRunning] = useState<string | null>(null);
  const [step, setStep] = useState("");
  const months = useMemo(() => monthOptions(), []);
  const [month, setMonth] = useState(months[0].value);
  const cutoff = months.find((m) => m.value === month)!.cutoff;

  const ready = !!source.data;
  const accounts = source.data?.accounts ?? [];

  async function run(key: string, job: () => Promise<number | void>) {
    setRunning(key);
    setStep("");
    try {
      const n = await job();
      notifyOk(typeof n === "number" ? t("informes.done", { n: fmt(n) }) : t("informes.doneFile"));
    } catch (e: any) {
      notifyError(e.message);
    } finally {
      setRunning(null);
    }
  }

  const fromCuadre: Record<"cuadre" | "descuadres" | "plataformas" | "facturacion", () => Promise<number>> = {
    cuadre: async () => {
      await downloadXlsxSheets(`cuadre_unidades_${fileDate()}.xlsx`, [unitsSheet(units)]);
      return units.length;
    },
    descuadres: async () => {
      const sheets = descuadresSheets(units, buildAccounts(units, accounts));
      await downloadXlsxSheets(`descuadres_${fileDate()}.xlsx`, sheets);
      return sheets.reduce((n, s) => n + s.rows.length, 0);
    },
    plataformas: async () => {
      await downloadXlsxSheets(`plataformas_por_cuenta_${fileDate()}.xlsx`, platformsSheets(units, accounts, retrans.data));
      return units.length;
    },
    facturacion: async () => {
      await downloadXlsxSheets(`facturacion_${fileDate()}.xlsx`, [facturacionSheet(units, accounts)]);
      return accounts.length;
    },
  };
  const fromSupabase: Record<"barrido" | "lineas" | "usuarios", () => Promise<number>> = {
    barrido: () => exportBarrido((stage, n) => setStep(t(`informes.steps.${stage}`, { n: fmt(n) }))),
    lineas: () => exportLineas((n) => setStep(t("informes.steps.devices", { n: fmt(n) }))),
    usuarios: () => exportUsuarios(usuariosHeaders(t)),
  };

  const card = isDarkMode ? "bg-dark-surface border-dark-border" : "bg-white border-gray-200";
  const muted = isDarkMode ? "text-gray-400" : "text-gray-500";
  const strong = isDarkMode ? "text-white" : "text-gray-900";
  const busy = running != null;

  const loadingNote = !ready && (
    <span className={`inline-flex items-center gap-1.5 text-xs ${muted}`}>
      <i className="ri-loader-4-line animate-spin" aria-hidden="true" />
      {source.isError ? (source.error as Error)?.message : t(`cuadre.loading.${progress.stage}`, { n: fmt(progress.n) })}
    </span>
  );

  return (
    <div className="h-full min-h-0 w-full overflow-y-auto">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6 space-y-6">
        <header>
          <h1 className={`text-2xl font-bold tracking-tight ${strong}`}>{t("informes.title")}</h1>
          <p className={`text-sm mt-1 ${muted}`}>{t("informes.subtitle")}</p>
        </header>

        {/* Barridos homologados: el informe mensual, por eso va primero y más grande */}
        <section className={`rounded-2xl border p-5 sm:p-6 grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)] ${card}`}>
          <div>
            <div className="flex items-center gap-2">
              <i className="ri-file-excel-2-line text-xl text-primary-500" aria-hidden="true" />
              <h2 className={`text-lg font-semibold ${strong}`}>{t("informes.homologado.title")}</h2>
            </div>
            <p className={`mt-2 text-sm leading-relaxed ${muted}`}>{t("informes.homologado.description")}</p>
            <p className={`mt-3 text-xs ${muted}`}>{t("informes.homologado.sheets")}</p>
            <label className="mt-4 block max-w-xs">
              <span className={`block text-xs font-medium mb-1 ${muted}`}>{t("informes.homologado.month")}</span>
              <select
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                className={`w-full h-10 px-3 rounded-xl border text-sm capitalize ${isDarkMode ? "bg-dark-card border-dark-border text-white" : "bg-gray-50 border-gray-200 text-gray-900"}`}
              >
                {months.map((m) => (
                  <option key={m.value} value={m.value}>
                    {barridoMonth(m.cutoff)}
                  </option>
                ))}
              </select>
              <span className={`block mt-1 text-xs ${muted}`}>{t("informes.homologado.cutoff", { date: cutoff.toLocaleDateString("es-MX"), interpolation: { escapeValue: false } })}</span>
            </label>
          </div>
          <div className="flex flex-col gap-2 justify-center">
            {PLATFORMS.map((p) => (
              <PlatformDownload
                key={p}
                platform={p}
                count={units.filter((u) => u.platforms.includes(p)).length}
                disabled={!ready || busy}
                loading={running === `h-${p}`}
                isDarkMode={isDarkMode}
                onClick={() => run(`h-${p}`, () => downloadHomologado(p, units, cutoff))}
              />
            ))}
            <button
              type="button"
              disabled={!ready || busy}
              onClick={() => run("h-zip", () => downloadHomologadoZip(units, cutoff))}
              className="mt-1 h-11 rounded-xl bg-primary-500 hover:bg-primary-400 text-black text-sm font-semibold flex items-center justify-center gap-2 transition-[background-color,transform] duration-150 active:scale-[0.98] disabled:opacity-50"
            >
              <i className={running === "h-zip" ? "ri-loader-4-line animate-spin" : "ri-folder-zip-line"} aria-hidden="true" />
              {t("informes.homologado.zip")}
            </button>
            {/* No depende de SG: lee Wialon en vivo, por eso no espera a `ready` */}
            <button
              type="button"
              disabled={busy}
              onClick={() => run("h-native", () => downloadNativoWialon(cutoff))}
              className={`group mt-2 min-h-12 px-4 py-2 rounded-xl border border-dashed flex items-center gap-3 text-left transition-[border-color,background-color,transform] duration-150 active:scale-[0.99] disabled:opacity-50 ${
                isDarkMode ? "border-dark-border hover:bg-dark-card" : "border-gray-300 hover:bg-gray-50"
              }`}
            >
              <PlatformLogo platform="wialon" size={20} />
              <span className="flex-1 min-w-0">
                <span className={`block text-sm font-medium ${strong}`}>{t("informes.homologado.native")}</span>
                <span className={`block text-xs ${muted}`}>{t("informes.homologado.nativeHint")}</span>
              </span>
              <i className={`${running === "h-native" ? "ri-loader-4-line animate-spin" : "ri-download-2-line"} ${muted}`} aria-hidden="true" />
            </button>
            {loadingNote}
          </div>
        </section>

        <ReportGroup title={t("informes.groups.cuadre")} isDarkMode={isDarkMode}>
          {(Object.keys(fromCuadre) as (keyof typeof fromCuadre)[]).map((k) => (
            <ReportCard
              key={k}
              k={k}
              icon={{ cuadre: "ri-dashboard-3-line", descuadres: "ri-error-warning-line", plataformas: "ri-stack-line", facturacion: "ri-bill-line" }[k]}
              disabled={!ready || busy}
              loading={running === k}
              isDarkMode={isDarkMode}
              onClick={() => run(k, fromCuadre[k])}
            />
          ))}
        </ReportGroup>

        <ReportGroup title={t("informes.groups.operacion")} isDarkMode={isDarkMode}>
          {(Object.keys(fromSupabase) as (keyof typeof fromSupabase)[]).map((k) => (
            <ReportCard
              key={k}
              k={k}
              icon={{ barrido: "ri-radar-line", lineas: "ri-sim-card-2-line", usuarios: "ri-user-line" }[k]}
              disabled={busy}
              loading={running === k}
              note={running === k && step ? <span className={`text-xs ${muted}`}>{step}</span> : undefined}
              isDarkMode={isDarkMode}
              onClick={() => run(k, fromSupabase[k])}
            />
          ))}
        </ReportGroup>
      </div>
    </div>
  );
}

function PlatformDownload({
  platform,
  count,
  disabled,
  loading,
  isDarkMode,
  onClick,
}: {
  platform: SgPlatform;
  count: number;
  disabled: boolean;
  loading: boolean;
  isDarkMode: boolean;
  onClick: () => void;
}) {
  const { t } = useTranslation("sg");
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`group h-12 px-4 rounded-xl border flex items-center gap-3 text-left transition-[border-color,background-color,transform] duration-150 active:scale-[0.99] disabled:opacity-50 ${
        isDarkMode ? "border-dark-border hover:bg-dark-card" : "border-gray-200 hover:bg-gray-50"
      }`}
    >
      <PlatformLogo platform={platform} size={20} />
      <span className={`flex-1 text-sm font-medium ${isDarkMode ? "text-gray-100" : "text-gray-900"}`}>{PLATFORM_META[platform].label}</span>
      <span className={`text-xs tabular-nums ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>{t("informes.units", { n: fmt(count) })}</span>
      <i className={`${loading ? "ri-loader-4-line animate-spin" : "ri-download-2-line"} ${isDarkMode ? "text-gray-400" : "text-gray-500"}`} aria-hidden="true" />
    </button>
  );
}

function ReportGroup({ title, isDarkMode, children }: { title: string; isDarkMode: boolean; children: ReactNode }) {
  return (
    <section>
      <h2 className={`mb-3 text-[11px] font-semibold uppercase tracking-wider ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>{title}</h2>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">{children}</div>
    </section>
  );
}

function ReportCard({
  k,
  icon,
  disabled,
  loading,
  note,
  isDarkMode,
  onClick,
}: {
  k: ReportKey;
  icon: string;
  disabled: boolean;
  loading: boolean;
  note?: ReactNode;
  isDarkMode: boolean;
  onClick: () => void;
}) {
  const { t } = useTranslation("sg");
  return (
    <article className={`rounded-2xl border p-4 flex flex-col ${isDarkMode ? "bg-dark-surface border-dark-border" : "bg-white border-gray-200"}`}>
      <div className="flex items-center gap-2">
        <span className={`w-8 h-8 rounded-lg flex items-center justify-center ${isDarkMode ? "bg-white/5 text-gray-200" : "bg-gray-100 text-gray-700"}`}>
          <i className={icon} aria-hidden="true" />
        </span>
        <h3 className={`text-sm font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}>{t(`informes.reports.${k}.title`)}</h3>
      </div>
      <p className={`mt-2 text-xs leading-relaxed flex-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>{t(`informes.reports.${k}.description`)}</p>
      <div className="mt-3 flex items-center justify-between gap-2 min-h-[36px]">
        <span className="min-w-0 truncate">{note}</span>
        <button
          type="button"
          disabled={disabled}
          onClick={onClick}
          className={`shrink-0 h-9 px-3 rounded-xl border text-sm font-medium flex items-center gap-2 transition-transform active:scale-[0.97] disabled:opacity-50 ${
            isDarkMode ? "border-dark-border text-gray-200 hover:bg-dark-card" : "border-gray-200 text-gray-700 hover:bg-gray-50"
          }`}
        >
          <i className={loading ? "ri-loader-4-line animate-spin" : "ri-download-2-line"} aria-hidden="true" />
          {t("informes.download")}
        </button>
      </div>
    </article>
  );
}
